import React, { useState, useEffect, useContext } from "react"
import { Card, Container, Transition } from "semantic-ui-react"
import moment, { Moment } from "moment"
import { CampingStyleProps, CampProps, SiteProps } from "../../interfaces"
import Sites from "./Sites"
import Prerequisites from "./Prerequisites"
import SelectedSite from "./SelectedSite"
import api from "../../api2"
import { validElectric, validRigType } from "../../types"
import { PREREQUISITES } from "../../constants"
import { BookerContext } from "../../contexts/BookerContext"
import { useDebouncedCallback } from "use-debounce"
import PrerequisiteProps from "../../interfaces/PrerequisiteProps"
import BookerButton from "../ui/BookerButton"
import classNames from "../../utils/classNames"
import NoAvailableSites from "./NoAvailableSites"
import { Metadata } from "~/api2/types"
import ProcessingState from "../ui/Admin/ProcessingState"

interface ComponentProps {
  visible: boolean
  setActiveSite: any // TODO: proper function type
  camp: CampProps
  startDate: Moment | null
  endDate: Moment | null
  setShowDatesCard
  isShowingDatesCard: boolean

  userCampingStyle: CampingStyleProps

  userRigType: validRigType
  setUserRigType: (v: validRigType) => any
  userRigLength: number
  setUserRigLength
  userAmps: validElectric
  setUserAmps
  userPassengers: number
  setUserPassengers: (v: number) => any
  userKidCampers: number
  setUserKidCampers: (v: number) => any
  showPayment: boolean
  groupedPrerequisites: PrerequisiteProps[] | null
  groupId: string | null
  isSkippingSiteSelection: boolean
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>
}

const SitesCard = (props: ComponentProps) => {
  const [sites, setSites] = useState<SiteProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<Metadata>()
  const [activeSite, setActiveSite] = useState<SiteProps>()
  const [firstAvailableSite, setFirstAvailableSite] = useState<SiteProps>()
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(30)
  const { userPets } = useContext(BookerContext)
  const [notMatchAmperage, setNotMatchAmperage] = useState(false)
  const [preSelectedSite, setPreSelectedSite] = useState<SiteProps>(null)
  const [openedSite, setOpenedSite] = useState<SiteProps>(null)
  const [sitePhotos, setSitePhotos] = useState<Record<string, string[]>>({})

  const {
    startDate,
    endDate,
    groupedReservationId,
    setGroupedReservationId,
    selectedInterval,
    campingStyle,
    selectedSite,
    trackForCamp,
    trackForPark,
  } = useContext(BookerContext)

  const nights: number = startDate && endDate && moment(endDate).hours(12).diff(moment(startDate).hours(12), "days")

  let prereqs = props.groupedPrerequisites || props.userCampingStyle.prerequisites

  if (groupedReservationId || props.groupId) {
    prereqs = prereqs?.filter(
      (prereq) => prereq.kind === PREREQUISITES.PETS || prereq.kind === PREREQUISITES.PASSENGERS,
    )
  }

  const prereqsAreFulfilled = () => {
    if (
      props.groupId?.length &&
      props.groupedPrerequisites?.filter(
        (prereq) => prereq.kind === PREREQUISITES.PETS || prereq.kind === PREREQUISITES.PASSENGERS,
      )?.length
    )
      return false
    if (prereqs?.length <= 0) return true

    const requiredFields = prereqs.map((pr) => pr.kind)
    if (requiredFields.includes("max_rig_length") && !props.userRigLength) return false
    if (requiredFields.includes("electric") && !props.userAmps) return false
    if (requiredFields.includes(PREREQUISITES.PASSENGERS) && !props.userPassengers) return false
    if (requiredFields.includes(PREREQUISITES.PETS) && (userPets === null || userPets === undefined)) return false

    return true
  }

  const [showPrerequisitesScreen, setShowPrerequisitesScreen] = useState<boolean>(!prereqsAreFulfilled())

  const [transition, setTransition] = useState(false)

  // NOTE: recalculate if we should show screen if the camping style changes
  useEffect(() => {
    setShowPrerequisitesScreen(!prereqsAreFulfilled())
  }, [props.userCampingStyle])

  useEffect(() => {
    setLoading(true)
    const isElectricPrereqRequired = campingStyle.prerequisites.find((prereq) => prereq.kind === PREREQUISITES.ELECTRIC)

    if (
      (isElectricPrereqRequired && !props.userAmps && !(groupedReservationId || props.groupId)) ||
      props.isShowingDatesCard
    )
      return

    debouncedSites()
  }, [
    props.startDate,
    props.endDate,
    props.userRigType,
    props.userRigLength,
    props.userAmps,
    props.userCampingStyle,
    props.userPassengers,
    props.userKidCampers,
    groupedReservationId,
    props.visible,
    props.isShowingDatesCard,
    showPrerequisitesScreen,
    page,
    perPage,
  ])

  useEffect(() => {
    props.setActiveSite(activeSite)
  }, [activeSite])

  // Used to remove activeSite if the card is turning visible and already has activeSite
  useEffect(() => {
    if (props.visible && activeSite) {
      setActiveSite(null)
    }
  }, [props.visible])

  useEffect(() => {
    if (props.isSkippingSiteSelection && prereqsAreFulfilled() && startDate && endDate && firstAvailableSite) {
      props.setActiveSite(firstAvailableSite)
      props.setShowDatesCard(false)
    }

    setShowPrerequisitesScreen(!prereqsAreFulfilled())
  }, [prereqsAreFulfilled()])

  useEffect(() => {
    if (firstAvailableSite && campingStyle?.name !== firstAvailableSite?.kind) {
      setFirstAvailableSite(undefined)
    }
  }, [firstAvailableSite])

  const debouncedSites = useDebouncedCallback(async () => {
    return (
      startDate &&
      endDate &&
      api
        .fetchSites(props.camp.slug, props.startDate?.toISOString() as string, props.endDate?.toISOString() as string, {
          campingStyleId: props.userCampingStyle.id,
          rigType: props.userRigType,
          rigLength: props.userRigLength,
          electric: props.userAmps,
          numberOfAdults: props.userPassengers,
          numberOfKids: props.userKidCampers,
          groupedReservationId,
          intervalId: selectedInterval?.id,
          page,
          perPage,
        })
        .then((data) => {
          if (data.sites[0]?.electric && !new RegExp(`\\b${props.userAmps}\\b`, "i").test(data.sites[0].electric)) {
            setNotMatchAmperage(true)
          } else setNotMatchAmperage(false)

          if (props.isSkippingSiteSelection) {
            const matchingAvailableSites = data.sites.filter((site) => {
              if (!site.is_available) return false

              // Verify electric requirements match exactly
              if (props.userAmps && site.electric &&
                !new RegExp(`\\b${props.userAmps}\\b`, "i").test(site.electric)) {
                return false
              }

              if (props.userRigLength && site.max_rig_length &&
                site.max_rig_length < props.userRigLength) {
                return false
              }

              return true
            });

            const firstAvailableSite = matchingAvailableSites[0]
            if (firstAvailableSite) {
              setFirstAvailableSite(firstAvailableSite)
              if (prereqsAreFulfilled()) {
                setActiveSite(firstAvailableSite)
              }
            } else {
              setSites([])
            }
          } else {
            if (props.visible && !showPrerequisitesScreen) {
              trackForCamp("view_item_list")
              trackForPark("view_item_list", {
                camp: props.camp.name,
              })
            }
            setSites(data.sites)
            setPaginationMeta(data.meta)
          }
          setLoading(false)
        })
        .catch((err) => {
          console.error(err)
          setLoading(false)
        })
    )
  }, 100)

  useEffect(() => {
    setActiveSite(null)
    setPreSelectedSite(null)
    setFirstAvailableSite(undefined)
  }, [campingStyle])

  const changeSite = () => {
    if (props.showPayment) {
      trackForCamp("remove_from_cart")
      trackForPark("remove_from_cart", {
        camp: props.camp.name,
      })
    }
    setActiveSite(null)
  }

  const cardPageClasses = () => {
    return (props.visible ? "visible transition" : "hidden transition") + (!activeSite ? " active" : "")
  }

  const SitesContainer = () =>
    (loading && props.isSkippingSiteSelection) ? (
      <ProcessingState
        loadingMessages={[
          "Finding the best site for you",
          "Checking availability",
          "Calculating the best price",
          "Almost there!",
        ]}
        successMessage={undefined}
      />
    ) :
      !activeSite ? (
        props.isSkippingSiteSelection ? (
          <NoAvailableSites
            setShowDatesCard={props.setShowDatesCard}
            groupedReservationId={groupedReservationId}
            setGroupedReservationId={setGroupedReservationId}
          />
        ) : (
          <Sites
            setActiveSite={setActiveSite}
            camp={props.camp}
            notMatchAmperage={notMatchAmperage}
            userCampingStyle={props.userCampingStyle}
            nights={nights}
            sites={sites}
            paginationMeta={paginationMeta}
            setShowDatesCard={props.setShowDatesCard}
            userRigType={props.userRigType}
            setUserRigType={props.setUserRigType}
            userRigLength={props.userRigLength}
            setUserRigLength={props.setUserRigLength}
            userAmps={props.userAmps}
            setUserAmps={props.setUserAmps}
            loading={loading}
            preSelectedSite={preSelectedSite}
            setPreSelectedSite={setPreSelectedSite}
            openedSite={openedSite}
            setOpenedSite={setOpenedSite}
            sitePhotos={sitePhotos}
            setSitePhotos={setSitePhotos}
            page={page}
            setPage={setPage}
            perPage={perPage}
          />
        )
      ) : (
        <SelectedSite site={activeSite} changeSiteCallback={changeSite} />
      )

  const sitePhotosArray = sitePhotos?.[openedSite?.id]

  const fetchImages = useDebouncedCallback(() => {
    if (!sitePhotosArray && !sitePhotosArray?.length && openedSite?.id) {
      api.Sites.fetchImages(openedSite.id)
        .then(({ images }) => {
          setSitePhotos((prev) => ({
            ...prev,
            [openedSite.id]: images.map((image) => image.url) || [],
          }))
        })
        .catch((error) => {
          console.log(error)
        })
    }
  }, 100)

  useEffect(() => {
    fetchImages()
  }, [openedSite])

  if ((activeSite || selectedSite) && prereqsAreFulfilled() && props.isSkippingSiteSelection) {
    return <></>
  }

  return (
    <Card fluid className={cardPageClasses()} id="sitesCard" style={{ overflow: "visible" }}>
      <Card.Content className="h-full relative" style={{ marginBottom: showPrerequisitesScreen && 65 }}>
        {showPrerequisitesScreen ? (
          <Prerequisites
            camp={props.camp}
            campingStyle={props.userCampingStyle}
            prerequisites={prereqs}
            setUserRigType={props.setUserRigType}
            setUserRigLength={props.setUserRigLength}
            setUserAmps={props.setUserAmps}
            setUserPassengers={props.setUserPassengers}
            setUserKidCampers={props.setUserKidCampers}
            setShowPrerequisites={setShowPrerequisitesScreen}
          />
        ) : (
          <></>
        )}
        <Transition visible={!showPrerequisitesScreen} animation="fade right" duration={props.isSkippingSiteSelection ? 0 : 500}>
          <Container className="transition-container">
            <SitesContainer />
          </Container>
        </Transition>
      </Card.Content>
      {!activeSite && !showPrerequisitesScreen && (
        <BottomBar
          onClick={() => {
            props.setShowPayment(true)
            setActiveSite(preSelectedSite)
            trackForCamp("add_to_cart")
            trackForPark("add_to_cart", {
              camp: props.camp.name,
            })
          }}
          ctaText="Next"
          disabled={!preSelectedSite}
          LeftComponent={() => (
            <div>
              {preSelectedSite ? (
                <p
                  className={classNames(
                    "text-gray-500",
                    preSelectedSite.calculated_nightly_price_in_cents == 0 && "invisible",
                  )}
                >
                  {preSelectedSite?.name}
                </p>
              ) : (
                <div className="text-gray-500">No site selected</div>
              )}
            </div>
          )}
        />
      )}
    </Card>
  )
}

const BottomBar = ({
  ctaText,
  disabled,
  onClick,
  LeftComponent,
}: {
  ctaText: string
  disabled: boolean
  onClick: () => void
  LeftComponent?: () => JSX.Element
}) => {
  const { secondaryColor } = useContext(BookerContext)

  return (
    <div
      className="fixed bottom-0 z-50 left-0 w-full py-3.5 px-5 flex items-center justify-between bg-white border-t"
      style={{ zIndex: 1000, borderColor: "rgba(34, 36, 38, .15)" }}
    >
      {LeftComponent ? <LeftComponent /> : <div></div>}
      <BookerButton
        disabled={disabled}
        onClick={onClick}
        className={"relative"}
        secondaryColor={secondaryColor}
        buttonText={ctaText}
        dataTestId="select-site-button"
      />
    </div>
  )
}

export default SitesCard
